"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useAuth } from "../../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../../services/api";
import { getUserTypeColor, hexToRgb } from "../../../utils/userTypeUtils";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../../hooks/useFlatpickrUserType";
import "./ChannelEventsModal.css";
import ConfirmModal from "./ConfirmModal";

const ChannelEventItem = ({ event, index, onUpdate, onRemove, t, userTypeColor }) => {
  const dateRef = useRef(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!dateRef.current) return;

    pickerRef.current = flatpickr(dateRef.current, {
      dateFormat: "Y-m-d",
      defaultDate: event.date || null,
      minDate: "today",
      allowInput: true,
      onChange: (selectedDates, dateStr) => {
        onUpdate(event.id, "date", dateStr);
      },
      onReady: (selectedDates, dateStr, instance) => {
        instance.calendarContainer.style.fontFamily = '"Red Hat Display", sans-serif';
        instance.calendarContainer.style.setProperty('--user-type-color', userTypeColor);
      }
    });

    return () => {
      if (pickerRef.current) {
        pickerRef.current.destroy();
      }
    };
  }, [userTypeColor, event.id, onUpdate]);

  // Update picker date if changed externally
  useEffect(() => {
    if (pickerRef.current && event.date && pickerRef.current.input.value !== event.date) {
      pickerRef.current.setDate(event.date, false);
    }
  }, [event.date]);

  return (
    <div className="channel-events-item">
      <div className="channel-events-item-header">
        <div className="channel-event-card-number">Event {index + 1}</div>
        <button
          type="button"
          className="channel-events-remove"
          onClick={() => onRemove(event.id)}
          aria-label={t("common.remove", "Remove")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
      
      <div className="channel-event-fields">
        <div className="channel-event-field">
          <div className="channel-event-field-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
          </div>
          <input
            type="text"
            className="channel-events-input"
            placeholder={t(
              "channelDetail.sidebar.events.fields.title",
              "Event title"
            )}
            value={event.title}
            onChange={(e) =>
              onUpdate(event.id, "title", e.target.value)
            }
            required
          />
        </div>

        <div className="channel-events-row">
          <div className="channel-event-field">
            <div className="channel-event-field-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <input
              ref={dateRef}
              type="text"
              className="channel-events-input"
              placeholder="YYYY-MM-DD"
              readOnly
              style={{ cursor: 'pointer' }}
              value={event.date || ""}
            />
          </div>
          <div className="channel-event-field">
            <div className="channel-event-field-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <input
              type="text"
              className="channel-events-input"
              placeholder={t(
                "channelDetail.sidebar.events.fields.location",
                "Location (optional)"
              )}
              value={event.location || ""}
              onChange={(e) =>
                onUpdate(event.id, "location", e.target.value)
              }
            />
          </div>
        </div>

        <div className="channel-event-field channel-event-field-textarea">
          <div className="channel-event-field-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="10" x2="3" y2="10"></line>
              <line x1="21" y1="6" x2="3" y2="6"></line>
              <line x1="21" y1="14" x2="3" y2="14"></line>
              <line x1="21" y1="18" x2="3" y2="18"></line>
            </svg>
          </div>
          <textarea
            className="channel-events-textarea"
            placeholder={t(
              "channelDetail.sidebar.events.fields.description",
              "Description or notes (optional)"
            )}
            value={event.description || ""}
            onChange={(e) =>
              onUpdate(event.id, "description", e.target.value)
            }
            rows={2}
          />
        </div>
      </div>
    </div>
  );
};

const ChannelEventsModal = ({
  isOpen,
  onClose,
  channelId,
  initialEvents = [],
  onEventsUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const { getUserType } = useAuth();

  const currentUserType = getUserType();
  const userTypeColor = useFlatpickrUserType(); // Use the hook for consistent calendar coloring

  const [events, setEvents] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eventToDeleteId, setEventToDeleteId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setEvents(
        Array.isArray(initialEvents)
          ? initialEvents.map((event) => ({
              id: event.id || event.title || Math.random().toString(36),
              title: event.title || "",
              date: event.date || "",
              location: event.location || "",
              description: event.description || "",
            }))
          : []
      );
    }
  }, [isOpen, initialEvents]);

  if (!isOpen) return null;

  const handleAddEvent = () => {
    setEvents((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        title: "",
        date: "",
        location: "",
        description: "",
      },
    ]);
  };

  const handleUpdateEvent = (id, field, value) => {
    setEvents((prev) =>
      prev.map((event) =>
        event.id === id ? { ...event, [field]: value } : event
      )
    );
  };

  const handleRemoveEvent = (id) => {
    setEventToDeleteId(id);
  };

  const confirmDelete = () => {
    if (eventToDeleteId) {
      setEvents((prev) => prev.filter((event) => event.id !== eventToDeleteId));
      setEventToDeleteId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!channelId) return;

    setIsSubmitting(true);
    try {
      // Clean up local event state before syncing
      const sanitizedEvents = events
        .filter(ev => ev.title.trim())
        .map(ev => ({
          id: ev.id,
          title: ev.title.trim(),
          date: ev.date || null,
          location: ev.location?.trim() || null,
          description: ev.description?.trim() || null
        }));

      // Perform a single bulk sync call
      const response = await channelsAPI.syncChannelEvents(channelId, sanitizedEvents);
      
      if (onEventsUpdated) {
        const rawEvents = response?.data?.data || [];
        
        const normalized = Array.isArray(rawEvents)
          ? rawEvents.map((event) => {
              if (typeof event === "string") {
                return { id: event, title: event, date: null, location: null, description: null };
              }

              return {
                id: event.id || event.title || event.name,
                title: event.title || event.name || "",
                date: event.date || event.starts_at || null,
                location: event.location || event.venue || null,
                description: event.description || null,
              };
            })
          : [];

        onEventsUpdated(normalized);
      }

      onClose();
    } catch (error) {
      console.error("Failed to sync events:", error);
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div
      className="channel-events-modal-overlay"
      onClick={onClose}
      dir={i18n.language?.startsWith("ar") ? "rtl" : "ltr"}
    >
      <div
        className="channel-events-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ 
          "--user-type-color": userTypeColor,
          "--user-type-color-rgb": hexToRgb(userTypeColor)
        }}
      >
        <div className="channel-events-modal-header">
          <h2 className="channel-events-modal-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {t("channelDetail.sidebar.events.addTitle", "Add event")}
          </h2>
          <button
            type="button"
            className="channel-events-modal-close"
            onClick={onClose}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="channel-events-modal-form">
          <div className="channel-events-list">
            {events.map((event, index) => (
              <ChannelEventItem 
                key={event.id}
                event={event}
                index={index}
                onUpdate={handleUpdateEvent}
                onRemove={handleRemoveEvent}
                t={t}
                userTypeColor={userTypeColor}
              />
            ))}

            {events.length === 0 && (
              <div className="channel-events-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '8px', opacity: 0.6 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <p style={{ margin: 0 }}>
                  {t(
                    "channelDetail.sidebar.events.empty",
                    "Plan a live session or office hours and it will show up here."
                  )}
                </p>
              </div>
            )}

            <button
              type="button"
              className="channel-events-add-card"
              onClick={handleAddEvent}
            >
              <div className="channel-events-add-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </div>
              <span>
                {t(
                  "channelDetail.sidebar.events.addAnother",
                  "Add event"
                )}
              </span>
            </button>
          </div>

          <div className="channel-events-modal-actions">
            <button
              type="button"
              className="channel-events-cancel"
              onClick={onClose}
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              className="channel-events-submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("common.saving", "Saving...")
                : t("channelDetail.sidebar.events.save", "Save events")}
            </button>
          </div>
        </form>
      </div>

      <ConfirmModal
        isOpen={!!eventToDeleteId}
        title={t("channelDetail.sidebar.events.deleteTitle", "Delete Event")}
        message={t(
          "channelDetail.sidebar.events.deleteConfirmation",
          "Are you sure you want to delete this event?"
        )}
        confirmLabel={t("common.delete", "Delete")}
        cancelLabel={t("common.cancel", "Cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setEventToDeleteId(null)}
        showWarningIcon={true}
      />
    </div>
  );

  return createPortal(modal, document.body);
};

export default ChannelEventsModal;


